import { Box, Typography } from '@mui/material';
import moment from 'moment';
import ErrorIcon from '@mui/icons-material/Error';

import { FHTooltip } from '../../base';
import StatusIconRenderer from '../StatusIconRenderer';
import { formatEntityId, history } from '../../../helpers';
import { getTimeLabel } from './utils';
import { ServiceCodes } from '../billings/claims/constants';
import { calendarCardStyles, TIME_UNIT_HEIGHT } from './constants';
import { ENTITY_PREFIX, SupervisionNoteStatus, SupervisionType } from '../../../constants';
import {
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY3,
  FORTA_WHITE,
} from '../../../utils/colors';

import type { IGroupSupervision } from '../../../utils/types';

interface ICalendarCardProps {
  left?: number;
  width?: string;
  resource: IGroupSupervision;
  top?: number;
  units: number;
}

const CustomTooltip = (
  resource: IGroupSupervision,
  showNotConcludedAlert: boolean,
  onIdClick: () => void,
) => {
  const { status, clients, date_start } = resource;
  const { name, color } = calendarCardStyles[SupervisionType.GroupParent] || {};
  return (
    <Box
      data-testid="hdCalendarTooltip"
      sx={{ borderRadius: '4px', border: `1px solid ${FORTA_GRAY3}`, display: 'flex', p: 0.5 }}
    >
      <Box
        sx={{
          width: '5px',
          backgroundColor: color,
          borderRadius: '8px',
        }}
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StatusIconRenderer
              iconHeight={18}
              iconWidth={18}
              procedureCode={ServiceCodes.SessionProcedureCode}
              status={status}
            />
            <Typography ml={1}>{name}</Typography>
          </Box>
          <Typography
            color={FORTA_BLUE.primary}
            sx={{ fontSize: 14, ml: 3, cursor: 'pointer', textDecoration: 'underline' }}
            onClick={onIdClick}
          >
            {formatEntityId(String(resource.id), ENTITY_PREFIX.GroupSupervisionNotes)}
          </Typography>
        </Box>
        {showNotConcludedAlert && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ErrorIcon sx={{ color: FORTA_ERROR, width: '18px', height: '18px', mt: 2 }} />
            <Typography ml={1} mt={2}>
              Not concluded
            </Typography>
          </Box>
        )}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ mt: 1 }}>
            <Typography fontSize={10} width={100} color={FORTA_GRAY4}>
              Clients
            </Typography>

            {clients.length > 0
              ? clients.map((client, i) => {
                  return (
                    <Typography key={i} mt={i === 0 ? 0 : 1}>
                      {`${client?.first_name} ${client?.last_name} (ID: ${client?.id})`}
                    </Typography>
                  );
                })
              : '--'}
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography fontSize={10} width={35} color={FORTA_GRAY4}>
              Date
            </Typography>
            <Typography color={FORTA_GRAY4}>{moment(date_start).format('dddd MMMM Do')}</Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography fontSize={10} width={35} color={FORTA_GRAY4}>
              Time
            </Typography>
            <Typography color={FORTA_GRAY4}>{getTimeLabel(resource)}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

function GroupTrainingCalendarCard({ left, width, resource, units, top = 0 }: ICalendarCardProps) {
  const { clients, status, id } = resource;
  const { name, color } = calendarCardStyles[SupervisionType.GroupParent] || {};
  const height = TIME_UNIT_HEIGHT * units;

  const reduceTextSize = units < 3;
  const showNotConcludedAlert = status !== SupervisionNoteStatus.Completed;

  const showTimeLabel = height > 60;
  const showTypeLabel = height > 38;

  const handleNavigation = () => {
    history.navigate(`/group-parent-training/${id}`);
  };

  return (
    <FHTooltip
      sx={{ inset: 'auto auto -6px 0px', padding: '0px !important' }}
      title={CustomTooltip(resource, showNotConcludedAlert, handleNavigation)}
      placement="top-start"
      maxwidth={360}
      tooltipBorderRadius="8px"
      tooltipbackground={FORTA_WHITE}
      tooltipBoxShadow="0px 5px 5px rgba(0, 0, 0, 0.2)"
    >
      <Box
        sx={{
          top,
          left,
          position: 'absolute',
          width,
          borderTop: `1px solid ${FORTA_GRAY3}`,
          borderBottom: `1px solid ${FORTA_GRAY3}`,
          borderLeft: `4px solid ${color}`,
          borderRight: width !== '100%' ? `1px solid ${FORTA_GRAY3}` : 'none',
          backgroundColor: FORTA_BLUE.light3,
          height,
          overflow: 'hidden',
          display: 'flex',
          ':hover': {
            cursor: 'pointer',
            boxShadow: '0 4px 4px rgba(0, 0, 0, 0.1)',
            opacity: 0.9,
          },
        }}
        onClick={handleNavigation}
      >
        <Box sx={{ mx: 1, flexGrow: 1 }}>
          {showTypeLabel && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography fontSize={10} noWrap textAlign="left" flexGrow={1}>
                {name}
              </Typography>
              {showNotConcludedAlert && (
                <ErrorIcon
                  sx={{
                    color: FORTA_ERROR,
                    width: width !== '100%' ? '11px' : '16px',
                    height: width !== '100%' ? '11px' : '16px',
                  }}
                />
              )}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              noWrap
              textAlign="left"
              fontWeight={700}
              lineHeight={reduceTextSize ? '14px' : '24px'}
              fontSize={reduceTextSize ? 12 : 14}
            >
              {clients.length > 0
                ? `${clients.length} client${clients.length > 1 ? 's' : ''} participated`
                : 'No clients added'}
            </Typography>
            {showNotConcludedAlert && !showTypeLabel && (
              <ErrorIcon sx={{ color: FORTA_ERROR, width: '14px', height: '14px' }} />
            )}
          </Box>
          {showTimeLabel && (
            <Typography textAlign="left" fontSize={14}>
              {getTimeLabel(resource)}
            </Typography>
          )}
        </Box>
      </Box>
    </FHTooltip>
  );
}

export default GroupTrainingCalendarCard;
